import React, { useState } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import {
  Check,
  Zap,
  Crown,
  Building2,
  Users,
  Package,
  ShieldCheck,
  Search,
  ChevronRight,
  Star,
  TrendingUp,
  Cloud,
  Globe,
  Target,
  MessageSquare,
  CreditCard,
  Camera,
  FileText,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppIcon } from '../components/AppIcon';
import type { IconName } from '../assets/icons';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const PricingPage = () => {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const [selectedCategory, setSelectedCategory] = useState<'brokerages' | 'vendors'>('brokerages');

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-20 md:py-32 overflow-hidden">
        {/* Floating Background Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { Icon: Crown, delay: 0, x: '10%', y: '15%' },
            { Icon: Zap, delay: 0.5, x: '85%', y: '20%' },
            { Icon: Star, delay: 1, x: '15%', y: '70%' },
            { Icon: TrendingUp, delay: 1.5, x: '80%', y: '75%' },
            { Icon: Check, delay: 2, x: '50%', y: '10%' },
            { Icon: Building2, delay: 2.5, x: '90%', y: '50%' }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 0.15, 0],
                scale: [0, 1.5, 1],
                rotate: [0, 180, 360]
              }}
              transition={{
                duration: 4,
                delay: item.delay,
                repeat: Infinity,
                repeatDelay: 2
              }}
              style={{
                position: 'absolute',
                left: item.x,
                top: item.y
              }}
            >
              <item.Icon className="w-16 h-16 text-white" />
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Badge className="mb-6 bg-blue-500/20 text-white border-blue-400/30 px-4 py-2 backdrop-blur-sm">
                <Star className="w-4 h-4 mr-2 inline" />
                Flexible Plans for Every Team
              </Badge>
            </motion.div>

            <h1 className="text-white mb-6">
              Choose the Perfect Plan for Your Business
            </h1>

            <p className="text-blue-100 text-xl max-w-3xl mx-auto mb-8">
              From independent agents to multi-office brokerages and service vendors, find a plan that scales with your operations
            </p>

            {/* Category Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="inline-flex bg-white/10 backdrop-blur-md rounded-xl p-1.5 border border-white/20"
            >
              <button
                onClick={() => setSelectedCategory('brokerages')}
                className={`px-8 py-3 rounded-lg transition-all ${selectedCategory === 'brokerages'
                  ? 'bg-white text-blue-700 shadow-lg'
                  : 'text-white hover:bg-white/10'
                  }`}
              >
                <Building2 className="w-5 h-5 inline mr-2" />
                For Brokerages & Agents
              </button>
              <button
                onClick={() => setSelectedCategory('vendors')}
                className={`px-8 py-3 rounded-lg transition-all ${selectedCategory === 'vendors'
                  ? 'bg-white text-blue-700 shadow-lg'
                  : 'text-white hover:bg-white/10'
                  }`}
              >
                <Package className="w-5 h-5 inline mr-2" />
                For Vendors
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-16 md:h-24 fill-white" viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path d="M0,64 C360,96 720,96 1080,64 C1440,32 1440,32 1440,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* Brokerage & Agent Plans */}
      {selectedCategory === 'brokerages' && (
        <>
          {/* Main Pricing Cards */}
          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {/* Starter Plan */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                      <AppIcon name="pricingStarter" size={28} alt="Starter" />
                    </div>
                    <div>
                      <h3 className="text-gray-900">Starter</h3>
                      <p className="text-sm text-gray-500">Small teams</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm text-gray-700 mt-2">
                      Manage signage and installations efficiently
                    </p>
                  </div>

                  <Button className="w-full mb-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white">
                    Start Free Trial
                  </Button>

                  <div className="space-y-3">
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Includes:</p>
                    {[
                      'Up to 3 users',
                      'Manage installations and removals',
                      'Vendor marketplace access',
                      'Inventory tracking (50 items)',
                      'Basic notifications and scheduling',
                      'WorkDrive (5 GB)',
                      'Standard reports and mobile app'
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Professional Plan */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-2xl border-2 p-8 relative hover:shadow-3xl transition-all hover:-translate-y-2 overflow-hidden"
                >
                  {/* <div className="absolute -top-0 left-1/2 -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-1.5 shadow-lg">
                      <Star className="w-3 h-3 inline mr-1" />
                      Most Popular
                    </Badge>
                  </div> */}

                  {/* Subtle gradient top border */}
                  {/* <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600"></div> */}

                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                      <AppIcon name="pricingProfessional" size={28} alt="Professional" />
                    </div>
                    <div>
                      <h3 className="text-gray-900">Professional</h3>
                      <p className="text-sm text-gray-600">Growing brokerages</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm text-gray-700 mt-2">
                      Add automation, reporting, and WorkDrive
                    </p>
                  </div>

                  <Button className="w-full mb-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white">
                    Start Free Trial
                  </Button>

                  <div className="space-y-3">
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Everything in Starter, plus:</p>
                    {[
                      'Up to 10 users',
                      'Multi-office and role-based access',
                      'Smart scheduling and reminders',
                      'WorkDrive (50 GB)',
                      'Shared payments (office-agent split)',
                      'Advanced reporting (spend, jobs, inventory)',
                      'SmartRead AI (10 docs/month)',
                      'Priority notifications & vendor spend tracking'
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Enterprise Plan */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-lg border-2 p-8 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden"
                >
                  {/* Subtle gradient top border */}
                  {/* <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600"></div> */}

                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                      <AppIcon name="pricingEnterprise" size={28} alt="Enterprise" />
                    </div>
                    <div>
                      <h3 className="text-gray-900">Enterprise</h3>
                      <p className="text-sm text-gray-600">Multi-office firms</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl text-gray-900">Custom</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-2">
                      Full AI, automation, and custom integrations
                    </p>
                  </div>

                  <Button
                    className="w-full mb-6 text-white hover:opacity-90"
                    style={{ background: "linear-gradient(90deg, #073399 0%, #236DD9 100%)" }}
                  >
                    Contact Sales
                  </Button>

                  <div className="space-y-3">
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Everything in Professional, plus:</p>
                    {[
                      'Unlimited users',
                      'WorkDrive (1 TB)',
                      'Decode AI, SmartRead AI, and SmartShow AI',
                      'Custom QuickBooks, UPS, and CRM integrations',
                      'Advanced forecasting and dashboards',
                      'SSO & enterprise-grade security',
                      'Dedicated success manager'
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Add-Ons Section */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-12">
                <Badge className="mb-4 bg-blue-50 text-blue-700 border-blue-200 px-4 py-2">
                  <Zap className="w-4 h-4 mr-2 inline" />
                  Enhance Your Plan
                </Badge>
                <h2 className="text-gray-900 mb-4">Add-Ons & Upgrades</h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  Customize your plan with additional features as your needs grow
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                {(
                  [
                    {
                      icon: 'pricingExtraWorkdriveStorage',
                      name: 'Extra WorkDrive storage',
                      description: 'Expand your file storage capacity'
                    },
                    {
                      icon: 'pricingExtraAiDocumentCredits',
                      name: 'Extra AI document credits',
                      description: 'Process more documents with AI'
                    },
                    {
                      icon: 'pricingCustomDomainBranding',
                      name: 'Custom domain branding',
                      description: 'Use your own domain name'
                    },
                    {
                      icon: 'pricingMultiOfficeExpansionPack',
                      name: 'Multi-office expansion pack',
                      description: 'Add support for more offices'
                    }
                  ] as { icon: IconName; name: string; description: string }[]
                ).map((addon, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all hover:-translate-y-1"
                  >
                    <div className="w-12 h-12 bg-white rounded-lg shadow-md border border-gray-200 flex items-center justify-center mb-4">
                      <AppIcon name={addon.icon} size={28} alt={addon.name} />
                    </div>
                    <h3 className="text-gray-900 mb-4">{addon.name}</h3>
                    <p className="text-sm text-gray-600">{addon.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Vendor Plans */}
      {selectedCategory === 'vendors' && (
        <>
          {/* Visibility Explanation Banner */}
          <section className="py-12 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto text-center">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-gray-900">Visibility-Based Plans</h2>
                </div>
                <p className="text-gray-700 text-lg">
                  Your visibility determines how many agents, brokerages, and offices can discover your listings and services within the RealEsy ecosystem. The more visibility, the more opportunities.
                </p>
              </div>
            </div>
          </section>

          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {/* Vendor Basic */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                      <AppIcon name="pricingStarter" size={28} alt="Vendor Basic" />
                    </div>
                    <div>
                      <h3 className="text-gray-900">Vendor Basic</h3>
                      <p className="text-sm text-gray-500">Local vendors</p>
                    </div>
                  </div>

                  {/* Visibility Badge */}
                  <div className="mb-6">
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-5 h-5 text-[#f58b01]" />
                        <span className="text-sm text-gray-600">Visibility Reach</span>
                      </div>
                      <p className="text-gray-900">Up to 100 agents</p>
                      <p className="text-gray-900">10 brokerages</p>
                    </div>
                    <p className="text-sm text-gray-600">
                      Gain visibility and receive direct service orders
                    </p>
                  </div>

                  <Button className="w-full mb-6 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white">
                    Get Started
                  </Button>

                  <div className="space-y-3">
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Includes:</p>
                    {[
                      'Visibility to 100 agents & 10 brokerages',
                      'List up to 25 products/services',
                      'Receive and manage orders',
                      'Basic billing & payment dashboard',
                      'WorkDrive (5 GB)',
                      'Chat with brokerages and agents',
                      '2 team logins'
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Vendor Plus */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-2xl border-2 p-8 relative hover:shadow-3xl transition-all hover:-translate-y-2 overflow-hidden"
                >
                  {/* <div className="absolute -top-0 left-1/2 -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-sky-600 to-blue-600 text-white px-4 py-1.5 shadow-lg">
                      <Star className="w-3 h-3 inline mr-1" />
                      Best Value
                    </Badge>
                  </div> */}

                  {/* Subtle gradient top border */}
                  {/* <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-500 via-blue-500 to-sky-600"></div> */}

                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                      <AppIcon name="pricingProfessional" size={28} alt="Vendor Plus" />
                    </div>
                    <div>
                      <h3 className="text-gray-900">Vendor Plus</h3>
                      <p className="text-sm text-gray-600">Growing vendors</p>
                    </div>
                  </div>

                  {/* Visibility Badge */}
                  <div className="mb-6">
                    <div className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-5 h-5 text-sky-600" />
                        <span className="text-sm text-gray-600">Visibility Reach</span>
                      </div>
                      <p className="text-gray-900">Up to 500 agents</p>
                      <p className="text-gray-900">50 brokerages</p>
                    </div>
                    <p className="text-sm text-gray-700">
                      Automate sales, manage inventory, and gain analytics
                    </p>
                  </div>

                  <Button className="w-full mb-6 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white">
                    Get Started
                  </Button>

                  <div className="space-y-3">
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Everything in Basic, plus:</p>
                    {[
                      'Visibility to 500 agents & 50 brokerages',
                      'Unlimited service order management',
                      'Inventory and asset tracking',
                      'Discount management tools',
                      'Advanced reporting (sales, top clients, repeat orders)',
                      'WorkDrive (20 GB)',
                      'Multi-role team management (up to 5 members)',
                      'Integration with TaxCloud & Stripe',
                      'Insights into job inflow and region demand'
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Vendor Premium */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-lg border-2 p-8 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden"
                >
                  {/* Subtle gradient top border */}
                  {/* <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600"></div> */}

                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                      <AppIcon name="pricingEnterprise" size={28} alt="Vendor Premium" />
                    </div>
                    <div>
                      <h3 className="text-gray-900">Vendor Premium</h3>
                      <p className="text-sm text-gray-600">Established vendors</p>
                    </div>
                  </div>

                  {/* Visibility Badge */}
                  <div className="mb-6">
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-5 h-5 text-amber-600" />
                        <span className="text-sm text-gray-600">Visibility Reach</span>
                      </div>
                      <p className="text-gray-900">Unlimited visibility</p>
                      <p className="text-gray-600 text-sm">All active agents & brokerages</p>
                    </div>
                    <p className="text-sm text-gray-700">
                      Full integrations, AI, and marketing insights
                    </p>
                  </div>

                  <Button className="w-full mb-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
                    Contact Sales
                  </Button>

                  <div className="space-y-3">
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Everything in Plus, plus:</p>
                    {[
                      'Unlimited visibility across all active agents & brokerages',
                      'Featured placement in the Vendor Marketplace',
                      'Advanced analytics (conversion, order sources, revenue)',
                      'AI tools for predictive demand & quote automation',
                      'CRM and ERP integrations (QuickBooks, Xero, HubSpot)',
                      'WorkDrive (100 GB)',
                      'Dedicated support & quarterly insights review'
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* All Vendor Plans Include */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-12">
                <Badge className="mb-4 bg-green-50 text-green-700 border-green-200 px-4 py-2">
                  <ShieldCheck className="w-4 h-4 mr-2 inline" />
                  Standard With Every Vendor Plan
                </Badge>
                <h2 className="text-gray-900 mb-4">All Vendor Plans Include</h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  Core features every vendor gets, regardless of plan
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {(
                  [
                    { icon: 'pricingAccessToRealesyMarketplace', title: 'Access to RealEsy Marketplace', desc: 'Be discovered by brokerages' },
                    { icon: 'pricingDirectChatOrderTracking', title: 'Direct chat and order tracking', desc: 'Real-time communication' },
                    { icon: 'pricingSecurePaymentsStripe', title: 'Secure payments with Stripe', desc: 'Safe payment processing' },
                    { icon: 'pricingAutoTaxInvoiceGeneration', title: 'Auto tax & invoice generation', desc: 'Automated billing' },
                    { icon: 'pricingRealTimeJobUpdates', title: 'Real-time job updates', desc: 'Proof photo uploads' },
                    { icon: 'pricingRoleBasedTeamAccess', title: 'Role-based team access', desc: 'Manage team permissions' },
                  ] as { icon: IconName; title: string; desc: string }[]
                ).map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100 text-center"
                  >
                    <div className="w-12 h-12 bg-white rounded-lg shadow-md border border-gray-200 flex items-center justify-center mx-auto mb-4">
                      <AppIcon name={item.icon} size={28} alt={item.title} />
                    </div>
                    <h3 className="text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600 text-lg">
                Everything you need to know about our pricing
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white rounded-lg px-6 border border-gray-200">
                <AccordionTrigger className="hover:no-underline py-5">
                  <span className="text-gray-900">Can I switch plans later?</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  Yes! You can upgrade or downgrade your plan at any time. Changes will be prorated and reflected in your next billing cycle.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-lg px-6 border border-gray-200">
                <AccordionTrigger className="hover:no-underline py-5">
                  <span className="text-gray-900">Is there a free trial?</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  Absolutely! All paid plans come with a 14-day free trial. No credit card required to start.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-lg px-6 border border-gray-200">
                <AccordionTrigger className="hover:no-underline py-5">
                  <span className="text-gray-900">What payment methods do you accept?</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  We accept all major credit cards (Visa, Mastercard, American Express), ACH transfers, and wire transfers for Enterprise plans.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white rounded-lg px-6 border border-gray-200">
                <AccordionTrigger className="hover:no-underline py-5">
                  <span className="text-gray-900">Do you offer annual pricing?</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  Yes! Save 20% when you pay annually. Contact our sales team for annual pricing details.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white rounded-lg px-6 border border-gray-200">
                <AccordionTrigger className="hover:no-underline py-5">
                  <span className="text-gray-900">How does vendor visibility work?</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  Vendor visibility determines how many agents and brokerages can discover your services in the RealEsy Marketplace. Higher visibility means more exposure and potential orders. You can upgrade your visibility at any time.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-white rounded-lg px-6 border border-gray-200">
                <AccordionTrigger className="hover:no-underline py-5">
                  <span className="text-gray-900">Can brokerages and vendors work together on the same platform?</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  Yes! That's one of RealEsy's key strengths. Brokerages and vendors each have their own plans, but can collaborate seamlessly through our Vendor Marketplace and order management system.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-white mb-6">Ready to Get Started?</h2>
            <p className="text-blue-100 text-xl mb-8">
              Join hundreds of brokerages and vendors already streamlining their operations with RealEsy
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 text-lg">
                  Start Free Trial
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Schedule a Demo
                </Button>
              </Link>
            </div>
            <p className="text-blue-200 text-sm mt-6">
              No credit card required • 14-day free trial • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>


    </div>
  );
};

export default PricingPage;
